import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import TextareaOnboarding from "components/onboarding/TextareaOnboarding";
import KeyboardAwareBottomButton from "components/onboarding/KeyboardAwareBottomButton";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { userApi } from "api/userApi";
import { useRecoilState } from "recoil";
import { userSimpleAtom } from "recoil/userSimpleAtom";
import { PATH } from "routes/paths";
import FullPageWithHeaderAndProgress from "../../../pages/container/FullPageWithHeaderAndProgress";
import useAutoLoginV3 from "./useAutoLoginV3";
import useMixpanelMKT from "../../../hooks/useMixpanelMKT";
import useMixpanelProfileMkt from "../../../hooks/useMixpanelProfileMkt";

export default function NicknameSubmitV3() {
  const navigate = useNavigate();
  /* ➊ HTMLTextAreaElement 참조용 useRef 생성 */
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [text, setText] = useState<string>();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useRecoilState(userSimpleAtom);
  const { isChecking } = useAutoLoginV3();

  const setPromotionPeople = useMixpanelProfileMkt("v3");

  const trackMixpanel = useMixpanelMKT({
    eventName: "닉네임 제출 페이지 진입",
    version: "v3",
  });

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);

    useEffect(() => {
      if (ref.current) return;
      ref.current = true;

      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    trackMixpanel();
  });

  const { mutate: submit } = useMutation(
    (nickname: string) => userApi.submitNicknameV3(nickname),
    {
      onSuccess: (_, nickname) => {
        setLoading(false);

        navigate(PATH.v3Payment, { replace: true });

        setUser((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            nickname: nickname,
          };
        });

        setPromotionPeople({ $name: nickname });
      },
    }
  );

  function handleSubmit() {
    if (text && text.length >= 1) {
      setLoading(true);
      submit(text);
    }
  }

  /* ➋ 페이지 로드 후 자동으로 텍스트에어리어에 포커스 */
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, []);

  // 로그인 체크 및 리다이렉트
  useEffect(() => {
    if (isChecking) return;

    if (!user?.accessToken) {
      // 로그인 안 되어 있으면 로그인 페이지로
      navigate(PATH.v3Login, { replace: true });
    }
  }, [isChecking, user, navigate]);

  return (
    <FullPageWithHeaderAndProgress
      hideHeader={false}
      hideProgress={true}
      body={
        <Stack spacing={5} sx={{ height: "100%" }}>
          <CustomTypography
            variant="Subtitle"
            fontWeight={500}
            sx={{
              color: theme.vars.palette.Content.Strong,
            }}
          >
            디스턴싱에 오신 것을 환영해요. 코치 선생님이 나를 어떻게 부르면
            좋을까요?
          </CustomTypography>

          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Box
              sx={{
                width: "100px",
                height: "54px",
                backgroundColor: theme.vars.palette.Surface.Forward,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                boxShadow: isFocused
                  ? `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`
                  : `inset 0 0 0 0`,
              }}
            >
              <TextareaOnboarding
                initialValue={text}
                handleChange={(newValue: string) => setText(newValue)}
                isFocused={isFocused}
                setIsFocused={setIsFocused}
                emojiDisabled={true}
                readOnly={false}
                textAlign={"center"}
              />
            </Box>
            <Typography
              variant="Body"
              fontWeight={500}
              sx={{
                color: theme.vars.palette.Content.Strong,
              }}
            >
              님
            </Typography>
          </Stack>

          <KeyboardAwareBottomButton
            loading={loading || isChecking}
            disabled={isChecking || text === undefined || text === ""}
            onClick={handleSubmit}
            text={"다음"}
            highlightBackground={false}
          />
        </Stack>
      }
      disableBack={false}
      onBack={() => navigate(PATH.v3Product)} // 첫 번째 단계에서는 뒤로가기 버튼 숨김
    />
  );
}
